import { Avatar, InfiniteScrollGrid } from "@etframework/ui.components";
import { ColumnDirective } from "@syncfusion/ej2-react-grids";
import PropTypes from "prop-types";
import {
  Fragment,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useEffect
} from "react";
import { useTranslation } from "react-i18next";
import { FilterGroup } from "../GridFilters/FilterGroup";
import useEvent from "../../hooks/useEvent";
import { useAuth } from "oidc-react";

export const AccessModalGrid = forwardRef(
  (
    {
      selectUsersRef,
      selectGroupsRef = null,
      nameColumn = null,
      gridCustomData = null,
      permissionsData
    },
    ref
  ) => {
    const { t, i18n } = useTranslation(["AccessModal"]);
    const gridRef = useRef(null);
    const { Events, useRegisterCustomEvent } = useEvent();
    const { userData } = useAuth();

    const gridUsersData = useMemo(() => {
      if (gridCustomData == null) {
        return permissionsData;
      } else {
        return gridCustomData;
      }
    }, [permissionsData, gridCustomData]);

    const [gridCurrentValues, setGridCurrentValues] = useState(
      gridUsersData?.Entities ?? gridUsersData
    );

    const commonOptions = {
      align: "Left",
      type: "Button"
    };

    const searchSettings = {
      fields: ["FullName", "MemberOf"],
      operator: "contains",
      ignoreCase: true,
      align: "Right"
    };

    const deleteToolbarItem = {
      ...commonOptions,
      id: "deleteItem",
      visible: false,
      text: t("DeleteItem"),
      prefixIcon: `fa-regular fa-trash-can`,
      click: () => {
        if (gridRef.current) {
          const selectedRows = gridRef?.current
            ?.getComponentRef()
            ?.getSelectedRecords();

          if (selectedRows.length > 0) {
            const gridData = getData();
            const Id = selectedRows.map((row) => row.Id ?? row.UserId);

            const newGridData = gridData?.filter(
              (row) => !Id.includes(row.Id ?? row.UserId)
            );

            setData(newGridData);

            selectUsersRef?.current?.setUsersInGrid(newGridData);
          }
        }
      }
    };

    const toolbarItems = [deleteToolbarItem];

    const onRowSelecting = (args) => {
      // prevent the user from selecting themselves
      const currentUserId = userData?.profile?.sub;
      const isCurrentUser = args.data?.Id === currentUserId;

      args.cancel = isCurrentUser;
    };

    const onRowDataBound = (args) => {
      const currentUserId = userData?.profile?.sub;
      const rowUserId = args.data?.Id;

      if (currentUserId === rowUserId) {
        // Hide the checkbox to prevent the user from selecting themselves
        const checkboxElement = args.row.querySelector(
          ".e-rowcell .e-checkbox-wrapper"
        );
        if (checkboxElement) {
          checkboxElement.style.display = "none";
        }
      }
    };

    const onRowSelected = (_, selectedCount) => {
      let itemsToShow = [];
      if (selectedCount === 0) {
        itemsToShow = [];
      } else if (selectedCount >= 1) {
        itemsToShow = ["deleteItem"];
      }

      gridRef.current?.setToolbarItemVisibility(itemsToShow, true, true);
    };

    const onRowDeselected = (_, selectedCount) => {
      let itemsToShow = [];
      if (selectedCount === 0) {
        itemsToShow = ["search-input"];
      } else if (selectedCount >= 1) {
        itemsToShow = ["deleteItem"];
      }

      gridRef.current?.setToolbarItemVisibility(itemsToShow, true, true);
    };

    const nameTemplate = (props) => {
      return (
        <div className="d-flex align-items-center gap-3">
          <Avatar content={props.FullName ?? props.Name}></Avatar>
          <span>{props.FullName ?? props.Name}</span>
        </div>
      );
    };

    const refreshBoardPermissionsGrid = () => {
      gridRef.current?.getComponentRef().refresh();
    };

    useRegisterCustomEvent(
      Events.RefreshBoardPermissionGrid,
      refreshBoardPermissionsGrid
    );

    const getData = useCallback(() => {
      return gridRef?.current?.getGridData();
    }, []);

    const setData = useCallback((data) => {
      gridRef.current?.setData(data);
      setGridCurrentValues(data);
    }, []);

    const hideSelectAllCheckbox = () => {
      const gridElement = gridRef.current.getComponentRef()?.element;

      const checkboxHeader = gridElement?.querySelector(
        ".e-headercelldiv.e-headerchkcelldiv"
      );
      if (checkboxHeader) {
        checkboxHeader.style.display = "none";
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        setData,
        getData,
        getInitialData: () => gridUsersData?.Entities ?? gridUsersData,
        getGridCurrentData: () => {
          return gridCurrentValues;
        },
        setGridCurrentData: () => setGridCurrentValues,
        getComponentRef: () => gridRef.current
      }),
      [gridCurrentValues]
    );

    useEffect(() => {
      hideSelectAllCheckbox();
    }, []);

    return (
      <Fragment>
        <FilterGroup defaultFilterName="All" />
        <InfiniteScrollGrid
          id="grid-permission"
          ref={gridRef}
          dataSource={gridUsersData?.Entities ?? gridUsersData}
          toolbarItems={toolbarItems}
          showHeader={false}
          searchSettings={searchSettings}
          height="200px"
          width="720px"
          allowFiltering={true}
          rowSelected={onRowSelected}
          rowDeselected={onRowDeselected}
          rowSelecting={onRowSelecting}
          rowDataBound={onRowDataBound}
          language={i18n.language}
          created={() => {
            selectUsersRef.current?.setUsersInGrid(getData());
            selectGroupsRef?.current?.setGroupsInGrid(getData());
          }}
        >
          <ColumnDirective
            field="PermissionType"
            visible={false}
            showInColumnChooser={false}
          />
          <ColumnDirective
            field="Id"
            visible={false}
            showInColumnChooser={false}
          />
          <ColumnDirective
            field="Name"
            headerText={nameColumn ?? t("NameCol")}
            template={nameTemplate}
            filter={{
              type: "CheckBox"
            }}
          />
        </InfiniteScrollGrid>
      </Fragment>
    );
  }
);

AccessModalGrid.displayName = "AccessModalGrid";

AccessModalGrid.propTypes = {
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      PermissionType: PropTypes.number,
      ClientId: PropTypes.string,
      Name: PropTypes.string,
      PrincipalId: PropTypes.string,
      MemberOf: PropTypes.string
    })
  )
};
